import { Sparkles, Filter, X } from 'lucide-react'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import type { NewsSummary } from '../types/ai.types'

// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━
// AI Summary Card Component with Streaming Support
// ━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━

interface AISummaryCardProps {
  keyword: string
  summary?: NewsSummary
  isLoading?: boolean
  // Streaming support
  isStreaming?: boolean
  streamingText?: string
  onCancelStreaming?: () => void
}

export function AISummaryCard({
  keyword: _keyword,
  summary,
  isLoading,
  isStreaming,
  streamingText,
  onCancelStreaming,
}: AISummaryCardProps) {
  // Streaming mode: show progressive text with typing cursor
  if (isStreaming) {
    return (
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 dark:border-blue-800 dark:from-blue-950 dark:to-indigo-950">
        <CardHeader>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Sparkles className="h-5 w-5 animate-pulse text-blue-600 dark:text-blue-400" />
              <CardTitle className="text-lg">AI 요약 생성 중...</CardTitle>
            </div>
            {onCancelStreaming && (
              <Button
                variant="ghost"
                size="sm"
                onClick={onCancelStreaming}
                className="h-8 gap-1 px-2 text-xs"
              >
                <X className="h-3 w-3" />
                중단
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent>
          <div
            className="whitespace-pre-line rounded-md border border-gray-200 bg-white p-4 text-sm leading-relaxed dark:border-gray-700 dark:bg-gray-900"
            role="status"
            aria-live="polite"
            aria-atomic="false"
          >
            {streamingText}
            <span className="ml-1 inline-block h-4 w-2 animate-pulse bg-blue-600 dark:bg-blue-400">
              ▊
            </span>
          </div>
        </CardContent>
      </Card>
    )
  }

  // Loading mode: show skeleton
  if (isLoading) {
    return (
      <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 dark:border-blue-800 dark:from-blue-950 dark:to-indigo-950">
        <CardHeader>
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 animate-pulse text-blue-600 dark:text-blue-400" />
            <CardTitle className="text-lg">AI 요약 준비 중...</CardTitle>
          </div>
        </CardHeader>
        <CardContent>
          <div className="space-y-3">
            <div className="h-4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
            <div className="h-4 w-3/4 animate-pulse rounded bg-gray-200 dark:bg-gray-700" />
          </div>
        </CardContent>
      </Card>
    )
  }

  // Completed mode: show final summary
  if (!summary) {
    return null
  }

  return (
    <Card className="border-blue-200 bg-gradient-to-br from-blue-50 to-indigo-50 dark:border-blue-800 dark:from-blue-950 dark:to-indigo-950">
      {/* Header */}
      <CardHeader>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Sparkles className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            <CardTitle className="text-lg">AI 요약</CardTitle>
          </div>
          <Badge variant="secondary" className="text-xs">
            {summary.article_count}개 기사 분석
          </Badge>
        </div>
      </CardHeader>

      {/* Summary Content */}
      <CardContent>
        {/* Core Summary Only */}
        <div className="whitespace-pre-line rounded-md border border-gray-200 bg-white p-4 text-sm leading-relaxed dark:border-gray-700 dark:bg-gray-900">
          {summary.summary_text}
        </div>

        {/* Stats */}
        {summary.duplicate_count > 0 && (
          <div className="mt-3 flex items-center gap-1 text-xs text-gray-500 dark:text-gray-400">
            <Filter className="h-3 w-3" />
            중복 {summary.duplicate_count}개 제거됨
          </div>
        )}
      </CardContent>
    </Card>
  )
}
